import { useEffect, useState } from 'react';
import type { ArtworkAdmin } from '../../shared/types';
import { completeUpload, putToR2, updateArtwork } from '../lib/api';
import { processImage } from '../lib/image';
import { CategorySelect } from './CategorySelect';
import { TagInput } from './TagInput';

interface EditDialogProps {
  artwork: ArtworkAdmin;
  onClose: () => void;
  onSaved: () => void;
  categorySuggestions: string[];
  tagSuggestions: string[];
}

export function EditDialog({ artwork, onClose, onSaved, categorySuggestions, tagSuggestions }: EditDialogProps) {
  const [title, setTitle] = useState(artwork.title);
  const [description, setDescription] = useState(artwork.description ?? '');
  const [category, setCategory] = useState(artwork.category.name);
  const [tags, setTags] = useState<string[]>(artwork.tags.map((t) => t.name));
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!replaceFile) return setPreviewUrl(null);
    const url = URL.createObjectURL(replaceFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [replaceFile]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      let derivatives: Awaited<ReturnType<typeof processImage>> | null = null;
      if (replaceFile) {
        derivatives = await processImage(replaceFile);
      }
      const resp = await updateArtwork(artwork.id, {
        title: title.trim(),
        description: description.trim() || null,
        category: category.trim(),
        tags,
        replace_images: derivatives
          ? {
              thumb: {
                content_type: derivatives.thumb.content_type,
                width: derivatives.thumb.width,
                height: derivatives.thumb.height,
                size: derivatives.thumb.size,
              },
              full: {
                content_type: derivatives.full.content_type,
                width: derivatives.full.width,
                height: derivatives.full.height,
                size: derivatives.full.size,
              },
            }
          : undefined,
      });
      if (derivatives && resp.thumb_upload && resp.full_upload) {
        await Promise.all([
          putToR2({
            url: resp.thumb_upload.url,
            blob: derivatives.thumb.blob,
            headers: resp.thumb_upload.required_headers,
          }),
          putToR2({
            url: resp.full_upload.url,
            blob: derivatives.full.blob,
            headers: resp.full_upload.required_headers,
          }),
        ]);
        await completeUpload(artwork.id, {
          thumb_width: derivatives.thumb.width,
          thumb_height: derivatives.thumb.height,
          full_width: derivatives.full.width,
          full_height: derivatives.full.height,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-ink-900/40 p-4"
      onClick={() => !busy && onClose()}
    >
      <div
        className="my-12 w-full max-w-2xl rounded-3xl bg-white p-6 shadow-xl sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-2xl font-semibold">Edit artwork</h2>
          <button type="button" onClick={() => !busy && onClose()} aria-label="Close" className="rounded-full p-2 text-ink-500 hover:bg-canvas-100">
            ×
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Title</label>
              <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required />
            </div>
            <div>
              <label className="label">Category</label>
              <CategorySelect value={category} onChange={setCategory} suggestions={categorySuggestions} />
            </div>
          </div>

          <div>
            <label className="label">Description</label>
            <textarea className="input min-h-[6rem]" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div>
            <label className="label">Tags</label>
            <TagInput value={tags} onChange={setTags} suggestions={tagSuggestions} />
          </div>

          <div>
            <label className="label">Replace image (optional)</label>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <div className="flex flex-col gap-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-ink-500">Current</div>
                <div className="h-32 w-32 overflow-hidden rounded-xl border border-ink-900/10 bg-canvas-100">
                  {artwork.thumb_url ? (
                    <img src={artwork.thumb_url} alt="" className="h-full w-full object-cover" />
                  ) : null}
                </div>
              </div>
              {previewUrl && (
                <div className="flex flex-col gap-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-ink-500">New</div>
                  <div className="h-32 w-32 overflow-hidden rounded-xl border border-ink-900/10 bg-canvas-100">
                    <img src={previewUrl} alt="" className="h-full w-full object-cover" />
                  </div>
                </div>
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  onChange={(e) => setReplaceFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-ink-700 file:mr-3 file:rounded-full file:border-0 file:bg-ink-900 file:px-4 file:py-2 file:text-sm file:font-medium file:text-canvas-50 hover:file:bg-ink-700"
                />
                <p className="mt-2 text-xs text-ink-500">
                  Replacing will overwrite both the thumbnail and the display image
                  in storage.
                </p>
              </div>
            </div>
          </div>

          {error && <div className="rounded-xl border border-accent/30 bg-accent/5 p-3 text-sm text-accent">{error}</div>}

          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => !busy && onClose()} disabled={busy}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
